import { useToken } from "@chakra-ui/react";
import {
  bbox as getBounds,
  buffer as getBuffer,
  distance,
  Feature,
  Geometry,
  nearestPointOnLine,
  point,
  pointToLineDistance,
} from "@turf/turf";
import {
  getInitialViewState,
  LatLonType,
  useFeatureService,
} from "@ui/maps/common";
import { useCallback, useState } from "react";
import { type LayerProps, type MapLayerMouseEvent } from "react-map-gl";
import { getHazardMapLayerStyle, getHazardMapLegend } from "../helpers";

export interface UseHazardMapOptions {
  userLocation: LatLonType;
  features?: Feature<Geometry, any>[];

  distance?: number;
  category: string;

  displayName: string;

  layerProps?: LayerProps;

  color?: string;

  singularName?: string;

  zoom?: number;

  projectionEndpoint?: string;

  useServiceBounds?: boolean;
}
export function useHazardMap(url: string, options: UseHazardMapOptions) {
  const { userLocation } = options;
  const [hovered, setHovered] = useState<any | null>(null);
  const feature = options?.features?.[0];
  const initialViewState = getInitialViewState({
    latitude: userLocation.lat,
    longitude: userLocation.lon,
    zoom: options?.zoom || 7,
  });
  const [color] = useToken("colors", [options?.color || "blue.800"]);
  const buffer = getBuffer(
    point([userLocation.lon, userLocation.lat]),
    options?.distance || 5,
    {
      units: "miles",
    },
  );
  const bbox = getBounds(buffer);

  const service = useFeatureService({
    url,
    layer: {
      ...getHazardMapLayerStyle(feature?.geometry, color),
      ...(options?.layerProps as any),
      source: options.category,
      id: options.category,
      beforeId: "radius",
    },
    arcgisOptions: {
      geometryOptions: {
        latitude: userLocation.lat,
        longitude: userLocation.lon,
        radius: options?.distance || 5,
      },
      projectionEndpoint: options?.projectionEndpoint,
      useServiceBounds:
        typeof options?.useServiceBounds === "undefined"
          ? options.useServiceBounds
          : true,
    },
  });

  const resetViewState = () =>
    options?.zoom
      ? service.ref.current.easeTo({
          center: [userLocation.lon, userLocation.lat],
          zoom: options.zoom,
          duration: 800,
        })
      : service.ref.current.fitBounds(bbox, {
          padding: 25,
          duration: 800,
        });
  const legendRows = getHazardMapLegend(
    options.displayName,
    options?.features?.[0],
    color,
  );
  const onLoad = useCallback(() => {
    service.onLoad();
    resetViewState();
  }, [service]);

  const onMouseMove = useCallback(
    (event: MapLayerMouseEvent) => {
      const feature = event?.features?.[0];
      if (hovered?.id) {
        service.ref.current.setFeatureState(
          { source: options.category, id: hovered?.id },
          { hover: false },
        );
        setHovered(null);
      }
      if (feature?.geometry && feature?.id) {
        const qLoc = point([userLocation.lon, userLocation.lat]);
        const distanceFromUserLocation =
          feature?.geometry.type === "Point"
            ? distance(qLoc, feature.geometry, { units: "miles" })
            : feature?.geometry.type === "LineString"
            ? pointToLineDistance(qLoc, feature.geometry, { units: "miles" })
            : feature?.geometry.type === "MultiLineString"
            ? distance(qLoc, nearestPointOnLine(feature.geometry, qLoc), {
                units: "miles",
              })
            : "";
        setHovered({
          id: feature?.id,
          lat: event.lngLat.lat,
          lon: event.lngLat.lng,
          distance: distanceFromUserLocation
            ? `${
                options?.singularName ? options.singularName + " " : ""
              }${distanceFromUserLocation.toFixed(2)} Miles Away`
            : "",
        });
        service.ref.current.setFeatureState(
          {
            source: options.category,
            id: feature?.id,
          },
          { hover: true },
        );
      }
    },
    [hovered],
  );
  return {
    ref: service.ref,
    onLoad,
    resetViewState,
    buffer,
    hovered,
    initialViewState,
    onMouseMove,
    legendRows,
  };
}
